﻿import $ from 'jquery';
import 'jquery.validate.unobtrusive';

$.validator.setDefaults({
    highlight: element => $(element).closest('.form-group').addClass('has-error'),
    unhighlight: element => $(element).closest('.form-group').removeClass('has-error'),
    // Note: We don't want to validate hidden fields with a validate-hidden class if it is hidden by a parent element.
    // Note: [contenteditable="true"]:not([name]) fixes an issue with CKEditor - see https://github.com/jquery-validation/jquery-validation/issues/1875#issuecomment-314919732 for more information.
    // Note: [contenteditable="true"]:not([name]) * makes sure the same error above is not thrown when clicking the save button in the HTML preview extension.
    ignore: ':hidden *, :hidden:not(.validate-hidden), [data-val-group] *, [contenteditable="true"]:not([name]), [contenteditable="true"]:not([name]) *',
    // Note: You cannot use an arrow function otherwise "this" will point to the wrong instance.
    submitHandler(form) {
        return form.dispatchEvent(new CustomEvent('valid', { cancelable: true, detail: { trigger: this.submitButton, validator: this } }));
    }
});

// Note: You cannot put this in the setDefault method above as the unobtrusive validation library overrides it.
$.validator.unobtrusive.options = {
    invalidHandler: (e, validator) => e.target.closest('form').dispatchEvent(new CustomEvent('invalid', { detail: { trigger: /* NOT e.target */event.target, validator } }))
};

// Fixes the following issue http://stackoverflow.com/questions/6906725/unobtrusive-validation-in-chrome-wont-validate-with-dd-mm-yyyy.
$.validator.methods['date'] = () => true;

// Fixes the following issue https://stackoverflow.com/questions/30449026/the-field-must-be-a-number.
$.validator.methods['number'] = function(value, element) {
    return this.optional(element) || /-?(?:\d+|\d{1,3}(?:[\s\.,]\d{3})+)(?:[\.,]\d+)?$/.test(value);
}

// Fixes the same problem as numbers, but for the range validator.
$.validator.methods.range = function(value, element, param) {
    var globalizedValue = parseFloat(value.replace(',', '.'));
    return this.optional(element) || (globalizedValue >= param[0] && globalizedValue <= param[1]);
}

$.validator.addMethod('filetype', (value, element, allowedTypes) => {
    if (value == null || value.length == 0)
        return true;

    // Create the validation regular expression.
    const re = new RegExp('\.(' + allowedTypes.replaceAll(',', '|') + ')$', 'i');

    const files = element.files;

    for (let i = 0; i < files.length; i++) {
        if (files[i].name.match(re) == null)
            return false;
    }

    return true;
});

$.validator.addMethod('maxfilesize', (value, element, size) => {
    if (value == null || value.length == 0)
        return true;

    const files = element.files;

    for (let i = 0; i < files.length; i++) {
        if (files[i].size > size * 1024)
            return false;
    }

    return true;
});

$.validator.addMethod('maximagesize', (value, element, params) => {
    if (value == null || value.length == 0)
        return true;

    const dimensions = JSON.parse(element.dataset.dimensions);

    for (let i = 0; i < element.files.length; i++) {
        if (dimensions[i].width > params['width'] || dimensions[i].height > params['height'])
            return false;
    }

    return true;
});

$.validator.addMethod('minimagesize', (value, element, params) => {
    if (value == null || value.length == 0)
        return true;

    const dimensions = JSON.parse(element.dataset.dimensions);

    for (let i = 0; i < element.files.length; i++) {
        if (dimensions[i].width < params['width'] || dimensions[i].height < params['height'])
            return false;
    }

    return true;
});

$.validator.addMethod('mustbetrue', (value, element) => element.checked);

$.validator.addMethod('password', (value, element, params) => {
    if (value == null || value.length == 0)
        return true;

    // Make sure the password meets the minimum length.
    if (value.length < params['minlength'])
        return false;

    // Make sure the password includes numbers (if applicable).
    if (params['includenumbers'] === 'True' && !/\d/.test(value))
        return false;

    // Make sure the password includes special characters (if applicable).
    if (params['includespecialcharacters'] === 'True' && !/[!@#$%^&*\(\)_\+\-\={}<>,\.\|"'~`:;\\?\/\[\] ]/.test(value))
        return false;

    // Make sure the password includes uppercase characters (if applicable).
    if (params['includeuppercasecharacters'] === 'True' && !/[A-Z]/.test(value))
        return false;

    return true;
});

$.validator.addMethod('requiredif', function(value, element, params) {
    // Get the target value (as a string, as that's what actual value will be).
    const targetValue = (params['targetvalue'] ?? '').toString().toUpperCase();

    // Get the dependent control.
    const control = $(`[name$="${params['dependentproperty']}"]`);

    // Get the dependent value of the dependent control.
    const dependentValue = getValue(control)?.toUpperCase();

    // If the condition is true, reuse the existing required field validator functionality.
    if ((dependentValue === targetValue && params['negate'] === 'False') || (dependentValue !== targetValue && params['negate'] === 'True'))
        return $.validator.methods.required.call(this, value, element, params);

    return true;
});

function getValue(control) {
    switch (control.attr('type')) {
        case 'checkbox':
            return control.is(':checked') ? 'True' : 'False';
        case 'radio':
            return control.filter(':checked').val();
        default:
            return control.val();
    }
}

$.validator.unobtrusive.adapters.addSingleVal('filetype', 'allowedtypes');
$.validator.unobtrusive.adapters.addSingleVal('maxfilesize', 'size');
$.validator.unobtrusive.adapters.add('maximagesize', ['width', 'height'], options => {
    options.rules['maximagesize'] = options.params;
    options.messages['maximagesize'] = options.message;
});
$.validator.unobtrusive.adapters.add('minimagesize', ['width', 'height'], options => {
    options.rules['minimagesize'] = options.params;
    options.messages['minimagesize'] = options.message;
});
$.validator.unobtrusive.adapters.addBool('mustbetrue');
$.validator.unobtrusive.adapters.add('password', ['minlength', 'includenumbers', 'includespecialcharacters', 'includeuppercasecharacters'], options => {
    options.rules['password'] = options.params;
    options.messages['password'] = options.message;
});
$.validator.unobtrusive.adapters.add('requiredif', ['dependentproperty', 'targetvalue', 'negate'], options => {
    options.rules['requiredif'] = options.params;
    options.messages['requiredif'] = options.message;
});

export function parseDynamicContent(context) {
    // Remove the existing validators (if it has already been wired up).
    const form = $(context).closest('form') // Look for parent forms (including itself).
        .add($(context).find('form')) // Add any child forms within the context.
        .off('.validate') // This makes sure the events are removed. Alternatively we could call validate().destroy().
        .removeData('validator')
        .removeData('unobtrusiveValidation');

    // The jquery.validate.unobtrusive file parses the document before you say $.validator.setDefaults, therefore sometimes defaults are ignored (if the DOM ready event is triggered before the setDefaults is reached). Therefore we need to comment out the code in the jquery.validate.unobtrusive.js file to prevent this. If we upgrade this file with a newer version we need to either make sure we commented out this code again (https://github.com/aspnet/jquery-validation-unobtrusive/issues/88 is somewhat related).
    $.validator.unobtrusive.parse(form);

    // First hide the existing required field indicators.
    $('.form-group', context).removeClass('required');

    // Display the required field indicator.
    $('[data-val-required], [data-val-requiredif]', context).filter('[data-val="true"]').not('.ignore-required-indicator').closest('.form-group').addClass('required');
}